import { supabase } from "@internal/auth";
import {
  type ChatMessage,
  type Ingredient,
  MEAL_METRICS,
  type MealMetric,
} from "./schema";

export interface MealEstimate {
  name: string;
  comment: string;
  ingredients: Ingredient[];
  // Only present when the user stated them outright («ble 900 g, delte i
  // 4») — the estimator never guesses a finished weight (docs/adr/0006).
  // Meals have no use for them and ignore them.
  finishedGrams?: number;
  portions?: number;
}

export interface EstimateMealInput {
  transcript: ChatMessage[];
  // The chat component only — recipe components are never part of the
  // estimate the AI revises.
  previousEstimate?: { name: string; ingredients: Ingredient[] };
  // Current Matpresiseringer texts; omitted when the user has none.
  foodClarifications?: string[];
  // The meal's recipe components, rendered as a read-only context block
  // in the prompt; omitted when the meal has none.
  recipeComponents?: Array<{
    name: string;
    scale: number;
    ingredients: Ingredient[];
  }>;
}

const INVALID = "Estimering ga et ugyldig svar.";

function parseIngredient(raw: unknown): Ingredient {
  const ing = raw as Partial<Ingredient> | null;
  if (!ing || typeof ing.name !== "string" || ing.name.trim().length === 0) {
    throw new Error(INVALID);
  }
  if (
    typeof ing.grams !== "number" ||
    !Number.isFinite(ing.grams) ||
    ing.grams < 0
  ) {
    throw new Error(INVALID);
  }
  const metrics = {} as Record<MealMetric, number>;
  for (const metric of MEAL_METRICS) {
    const value = ing[metric];
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
      throw new Error(INVALID);
    }
    metrics[metric] = value;
  }
  return { name: ing.name.trim(), grams: ing.grams, ...metrics };
}

export async function estimateMeal(
  input: EstimateMealInput,
): Promise<MealEstimate> {
  const { data, error } = await supabase.functions.invoke("estimate-meal", {
    body: input,
  });
  if (error) throw new Error("Estimering feilet. Prøv igjen.");
  const estimate = data as Partial<MealEstimate> | null;
  if (
    !estimate ||
    typeof estimate.name !== "string" ||
    estimate.name.trim().length === 0 ||
    typeof estimate.comment !== "string" ||
    !Array.isArray(estimate.ingredients) ||
    estimate.ingredients.length === 0
  ) {
    throw new Error(INVALID);
  }
  return {
    name: estimate.name.trim(),
    comment: estimate.comment.trim(),
    ingredients: estimate.ingredients.map(parseIngredient),
    ...(isPositive(estimate.finishedGrams)
      ? { finishedGrams: estimate.finishedGrams }
      : {}),
    ...(isPositive(estimate.portions)
      ? { portions: Math.floor(estimate.portions) }
      : {}),
  };
}

function isPositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
